
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Heart, ArrowRight, Brain } from 'lucide-react';
import FamilyActivityWidget from './FamilyActivityWidget';
import { InviteCodeEditor } from '@/components/profile/editors/InviteCodeEditor';
import { useUser } from '@/contexts/UserContext';

interface OnboardingBenefitsProps {
  inviteCode?: string | null;
  isFamilyAdmin?: boolean;
  isSingleMemberFamily?: boolean;
  userId?: string;
}

const OnboardingBenefits: React.FC<OnboardingBenefitsProps> = ({
  inviteCode,
  isFamilyAdmin = false,
  isSingleMemberFamily = false,
  userId
}) => {
  const { familyActivity } = useUser();
  const shouldShowFamilyFeatures = familyActivity?.shouldShowFamilyFeatures || false;
  
  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold font-playfair">
          Additional Benefits
        </h2>
        <p className="text-muted-foreground">
          More features unlock as you plan more meals and use Forkast regularly
        </p>
      </div>
      
      {/* Family Activity Widget - Show when family is active */}
      {shouldShowFamilyFeatures && (
        <div className="mb-6">
          <FamilyActivityWidget shouldShow={shouldShowFamilyFeatures} />
        </div>
      )}
      
      <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Family Collaboration Benefit - Show invite code directly */}
        <Card className="h-full border-2 border-sage/30 bg-sage/5">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-sage/20 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-sage" />
              </div>
              <CardTitle className="text-lg text-foreground">
                Family Collaboration
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-foreground">
              {isSingleMemberFamily 
                ? "Invite your partner or family members to collaborate on recipes, meal plans, and shopping lists."
                : "Share with more family members to expand your collaboration."
              }
            </p>
            
            {inviteCode && userId ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-sage">Your Family Invite Code:</p>
                <InviteCodeEditor
                  userId={userId}
                  inviteCode={inviteCode}
                  isEditable={true}
                  isFamilyAdmin={isFamilyAdmin}
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Invite code will appear here once available
              </p>
            )}
          </CardContent>
        </Card>

        {/* Wellness Tracking Benefit - Locked with indicator */}
        <Card className="opacity-75 relative overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <Heart className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg text-muted-foreground">
                Wellness Insights
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Monitor your meal wellness and nutrition patterns. Get insights 
              to help you maintain a balanced diet.
            </p>
            {/* Desktop badge - bottom position */}
            <div className="hidden lg:block mt-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-medium shadow-sm">
                Unlocks after 1 week of planning
              </Badge>
            </div>
            {/* Mobile badge - top-right position */}
            <div className="lg:hidden absolute top-3 right-3 z-10">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-medium shadow-sm">
                Unlocks after 1 week of planning
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* AI-Powered Suggested Meals Benefit - Locked with indicator */}
        <Card className="opacity-75 relative overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <Brain className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg text-muted-foreground">
                AI Suggested Meals
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Get personalized dinner suggestions powered by AI. Discover new recipes 
              based on your family's eating patterns and preferences.
            </p>
            {/* Desktop badge - bottom position */}
            <div className="hidden lg:block mt-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-medium shadow-sm">
                Unlocks after 1 week of planning
              </Badge>
            </div>
            {/* Mobile badge - top-right position */}
            <div className="lg:hidden absolute top-3 right-3 z-10">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-medium shadow-sm">
                Unlocks after 1 week of planning
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingBenefits;
